import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import classNames from 'classnames'
import plur from 'plur'
import {memo, useMemo} from 'react'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'

import {useRunningBuildUpdater} from '../../../../containers/RunningBuildUpdater'
import {useSyncedBooleanQueryParam} from '../../../../hooks/routes'
import {useBuildData, useProjectData} from '../../../../hooks/useEntity'
import {getBuildLocator} from '../../../../rest/locators'
import {getOverviewHref} from '../../../../routes'
import {getBuild} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import type {BuildId} from '../../../../types'
import {UserProperties} from '../../../../utils/userProperties'
import ContentPanel from '../../../common/ContentPanel/ContentPanel'
import contentPanelStyles from '../../../common/ContentPanel/ContentPanel.css'
import {useSequenceLoaderReadyHandler} from '../../../common/SequenceLoader/SequenceLoader.hooks'
import {testDetailsHint} from '../../Hints/HintItems/Hints.buildOverview'
import {useHint} from '../../Hints/Hints.hooks'
import {useFetchBuildTestCounters} from '../Tests.hooks'
import TestsTree from '../TestsTree/TestsTree'
import {useFetchTreeData, useTestOccurrenceTreeArg} from '../TestsTree/TestsTree.hooks'
import {
  getRootTestCounters,
  getTestsLocator as getTestTreeLocator,
} from '../TestsTree/TestsTree.utils'

import type {TestsPreviewPanelCountsQuery} from './__generated__/TestsPreviewPanelCountsQuery.graphql'
import {getCountsVariables, testsPreviewPanelCountsQuery} from './TestsPreviewPanel.queries'
import {getAllTestsLocator, getOwnTestsLocator} from './TestsPreviewPanel.utils'
import type {TestsPreviewPanelCountersFragment$key} from './TestsPreviewPanelCounters/__generated__/TestsPreviewPanelCountersFragment.graphql'
import TestsPreviewPanelCounters from './TestsPreviewPanelCounters/TestsPreviewPanelCounters'

import styles from './TestsPreviewPanel.css'

import {useAppSelector} from 'src/hooks/react-redux'

type BaseProps = {
  buildId: BuildId
}
type Props = BaseProps & {
  className?: string
  seqKey?: string
  buildId: BuildId
  countsKey: TestsPreviewPanelCountersFragment$key | null
}

function TestsPreviewPanel({buildId, countsKey}: Props) {
  const buildTypeId = useAppSelector(state => getBuild(state, buildId)?.buildType)

  const composite = useBuildData({buildId}, build => build?.composite)
  const ownTestsLocator = getOwnTestsLocator(getBuildLocator(buildId))
  const allTestsLocator = composite ? ownTestsLocator : getAllTestsLocator(buildId)

  const [hideTestsFromDependencies, toggleHideTestsFromDependencies] = useSyncedBooleanQueryParam({
    param: 'hideTestsFromDependencies',
    userProperty: UserProperties.HIDE_TESTS_FROM_DEPENDENCIES,
  })

  const ownTestsTreeLocator = getTestTreeLocator({baseLocator: ownTestsLocator})
  const allTestsTreeLocator = getTestTreeLocator({baseLocator: allTestsLocator})

  useRunningBuildUpdater(buildId, window.GLOBAL_FETCH_DISABLED === true)

  const testsTabLink = `${getOverviewHref({buildId, buildTypeId})}?buildTab=tests`

  const projectId = useProjectData({buildId}, project => project?.id)
  const ownTestsArg = useTestOccurrenceTreeArg(ownTestsTreeLocator)
  const {ownFailedTestsCount, ownTestsReady} =
    restApi.endpoints.getTestOccurrenceTree.useQueryState(ownTestsArg, {
      selectFromResult: ({data}) => ({
        ownFailedTestsCount: getRootTestCounters(data?.tree.node)?.failed ?? 0,
        ownTestsReady: data != null,
      }),
    })
  const allTestsArg = useTestOccurrenceTreeArg(allTestsTreeLocator)
  const {allFailedTestsCount, allNewFailedTestsCount, all, allTestsReady} =
    restApi.endpoints.getTestOccurrenceTree.useQueryState(allTestsArg, {
      selectFromResult: ({data}) => {
        const counters = getRootTestCounters(data?.tree.node)
        return {
          allFailedTestsCount: counters?.failed ?? 0,
          allNewFailedTestsCount: counters?.newFailed ?? 0,
          all: counters?.all ?? 0,
          allTestsReady: data != null,
        }
      },
    })
  useFetchTreeData(ownTestsTreeLocator, buildId)
  useFetchTreeData(allTestsTreeLocator, buildId)

  const hasTestsFromDependencies = ownFailedTestsCount !== allFailedTestsCount //TODO !!!!!!!!!!!!!!
  const treeArg = ownFailedTestsCount > 0 && hideTestsFromDependencies ? ownTestsArg : allTestsArg

  const isReady = ownTestsReady && allTestsReady

  useHint(allFailedTestsCount > 0 ? testDetailsHint : null)
  useSequenceLoaderReadyHandler(isReady)

  const renderHeaderSnippet = useMemo(
    () =>
      !composite &&
      ownFailedTestsCount > 0 &&
      hasTestsFromDependencies && (
        <Checkbox
          containerClassName={styles.hideProblemsFromDependencies}
          checked={hideTestsFromDependencies}
          onChange={() => toggleHideTestsFromDependencies(!hideTestsFromDependencies)}
        >
          {'Hide from dependencies'}
        </Checkbox>
      ),
    [
      composite,
      ownFailedTestsCount,
      hasTestsFromDependencies,
      hideTestsFromDependencies,
      toggleHideTestsFromDependencies,
    ],
  )

  const labelId = 'buildTests'

  const renderHeading = useMemo(() => {
    if (allFailedTestsCount === 0 && ownFailedTestsCount === 0) {
      return <span id={labelId}>{'Tests'}</span>
    }

    return (
      <span id={labelId}>{`${allFailedTestsCount} Failed ${plur('Test', allFailedTestsCount)}${
        ownFailedTestsCount === 0 ? ' In Dependencies' : ''
      }${allNewFailedTestsCount > 0 ? `, ${allNewFailedTestsCount} new` : ''}`}</span>
    )
  }, [allFailedTestsCount, allNewFailedTestsCount, ownFailedTestsCount])

  const renderContent = useMemo(
    () => (
      <>
        <TestsTree
          labelId={labelId}
          key={treeArg.treeLocator}
          locator={treeArg.treeLocator}
          projectId={projectId}
          buildId={buildId}
          arg={treeArg}
        />
        <TestsPreviewPanelCounters
          className={classNames({[styles.counters]: allFailedTestsCount > 0})}
          countsKey={countsKey}
          testsTabLink={testsTabLink}
        />
      </>
    ),
    [allFailedTestsCount, buildId, projectId, testsTabLink, treeArg, countsKey],
  )

  const href =
    allFailedTestsCount > 0 ? `${testsTabLink}&status=failed` : `${testsTabLink}&status=all`

  return isReady && all > 0 ? (
    <div data-hint-container-id={testDetailsHint.id}>
      <ContentPanel
        panelType={UserProperties.EXPAND_BUILD_TESTS_SECTION}
        href={ownFailedTestsCount > 0 ? href : null}
        heading={renderHeading}
        headingClassName={classNames({[contentPanelStyles.errorHeading]: allFailedTestsCount > 0})}
        headerSnippet={renderHeaderSnippet}
        content={renderContent}
      />
    </div>
  ) : null
}

type ContainerProps = BaseProps & {
  seqKey?: string
  countsQuery: PreloadedQuery<TestsPreviewPanelCountsQuery>
  buildLocator?: string
}
export default memo(function TestsPreviewPanelContainer({
  countsQuery,
  buildId,
  buildLocator,
}: ContainerProps) {
  const {testOccurrences} = useFetchBuildTestCounters(
    buildId,
    buildLocator,
    testsPreviewPanelCountsQuery,
    countsQuery,
    getCountsVariables,
  )
  return <TestsPreviewPanel buildId={buildId} countsKey={testOccurrences?.testCounters ?? null} />
})
